/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IconButton } from '@fluentui/react/lib/Button';
import { IIconProps, Icon } from '@fluentui/react/lib/Icon';
import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { ISpinnerProps, Spinner } from '@fluentui/react/lib/Spinner';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { getScopedTheme, getStylist, important } from 'src/core/themes/luisStylist';
import { ToastStatus } from 'src/luis/types/stores/events/ToastStatus';

const { styleComponent } = getStylist('Toast');
const toastTheme = getScopedTheme('Toast');

const getToastColorByStatus = (status: ToastStatus) => {
    switch (status) {
        case 'completed':
            return toastTheme.successBackgroundColor;
        case 'failed':
            return toastTheme.failureBackgroundColor;
        case 'informative':
        case 'running':
        default:
            return toastTheme.inProgressBackgroundColor;
    }
};

export const FloatingToast = styleComponent<IStackProps>(Stack)<{ status: ToastStatus }>(
    'FloatingToastContainer',
    theme => ({
        backgroundColor: theme.toastBackgroundColor,
        width: theme.floatingContainerWidth,
        marginBottom: theme.floatingContainerMargin,
        padding: theme.floatingContainerPadding,
        boxShadow: theme.floatingContainerBoxShadow,
        pointerEvents: 'all',
        border: theme.toastBorder
    }),
    ({ customProps }) => ({ backgroundColor: getToastColorByStatus(customProps.status) })
);

export const PanelToast = styleComponent<IStackProps>(Stack)<{ status: ToastStatus }>(
    'PanelToastContainer',
    theme => ({
        marginTop: theme.floatingContainerMargin,
        backgroundColor: theme.toastBackgroundColor,
        padding: theme.floatingContainerPadding,
        pointerEvents: 'all',
        border: theme.toastBorder
    }),
    ({ customProps }) => ({ backgroundColor: getToastColorByStatus(customProps.status) })
);

export const DismissIcon = styleComponent<IButtonProps>(IconButton)('ToastDismiss', theme => ({
    width: theme.dismissIconWidth,
    height: theme.dismissIconHeight
}));

export const StatusIcon = styleComponent<IIconProps>(Icon)<{ status: ToastStatus }>(
    'ToastStatusIcon',
    theme => ({ marginRight: theme.iconMarginRight }),
    ({ customProps }, theme) => ({ color: getIconColorByStatus(customProps.status, theme) })
);

export const ToastSpinner = styleComponent<ISpinnerProps>(Spinner)('ToastSpinner', theme => ({ marginRight: theme.iconMarginRight }));

export const DateText = styleComponent<ITextProps>(Text)('ToastDate', theme =>
    important({ marginLeft: 'auto', whiteSpace: 'nowrap', color: theme.dateFontColor, marginRight: theme.dateMarginRight })
);

export const ToastTitleContainer = styleComponent<IStackProps>(Stack)('ToastTitleContainer', theme => ({
    marginBottom: theme.toastTitleMarginBottom
}));

export const ToastContent = styleComponent<IStackProps>(Stack)('ToastContent', theme => ({
    lineHeight: 1.3,
    color: theme.contentFontColor,
    fontSize: theme.contentFontSize,
    fontFamily: theme.contentFontFamily,
    marginLeft: theme.contentMarginLeft,
    marginBottom: theme.contentMarginBottom
}));

export const ToastLink = styleComponent<ILinkProps>(Link)('ToastLink', theme => ({ height: theme.toastButtonHeight }));

/**
 * Gets the icon color by the toast status.
 *
 * @param status The toast status.
 * @param theme The theme of colors to use.
 * @returns The color to use based on the status.
 */
const getIconColorByStatus = (status: ToastStatus, theme: { successColor: string; errorColor: string; progressColor: string }) => {
    switch (status) {
        case 'completed':
            return theme.successColor;
        case 'failed':
            return theme.errorColor;
        case 'running':
        default:
            return theme.progressColor;
    }
};
